import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Star, Search, CheckCircle, XCircle, Clock, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => { fetchReviews(); }, [statusFilter]);

  const fetchReviews = async () => {
    try {
      const url = statusFilter !== "all" ? `${API}/admin/reviews?status=${statusFilter}` : `${API}/admin/reviews`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) setReviews(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateStatus = async (reviewId, status) => {
    try {
      const res = await fetch(`${API}/admin/reviews/${reviewId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed");
      const updated = await res.json();
      setReviews((prev) => prev.map((r) => (r.review_id === reviewId ? updated : r)));
      toast.success(`Review ${status}`);
    } catch (err) { toast.error("Failed to update review"); }
  };

  const deleteReview = async (reviewId) => {
    try {
      const res = await fetch(`${API}/admin/reviews/${reviewId}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      setReviews((prev) => prev.filter((r) => r.review_id !== reviewId));
      toast.success("Review deleted");
    } catch (err) { toast.error("Failed to delete review"); }
  };

  const filtered = reviews.filter((r) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (r.user_name || "").toLowerCase().includes(q) || (r.comment || "").toLowerCase().includes(q);
  });

  const counts = {
    all: reviews.length,
    pending: reviews.filter((r) => r.status === "pending").length,
    approved: reviews.filter((r) => r.status === "approved").length,
    rejected: reviews.filter((r) => r.status === "rejected").length,
  };

  const statusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      approved: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
    };
    return styles[status] || styles.pending;
  };

  const statusIcon = (status) => {
    if (status === "approved") return <CheckCircle size={12} />;
    if (status === "rejected") return <XCircle size={12} />;
    return <Clock size={12} />;
  };

  const renderStars = (rating) => (
    <div className="flex gap-px">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} size={12} className={s <= rating ? "text-amber-400 fill-amber-400" : "text-gray-500"} />
      ))}
    </div>
  );

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-reviews">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Review Management</h1>
            <p className="text-sm text-gray-500 mt-1">Approve or reject customer reviews</p>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {["all", "pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setLoading(true); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === s ? "bg-green-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid={`filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)} ({counts[s]})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search by customer or comment..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-white" data-testid="review-search" />
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No reviews found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((review) => (
              <div key={review.review_id} className="bg-white border border-gray-100 rounded-xl p-4" data-testid={`review-${review.review_id}`}>
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center text-sm font-bold text-green-700">
                      {(review.user_name || "U").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{review.user_name}</p>
                      <p className="text-[10px] text-gray-500">{new Date(review.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(review.status)}`}>
                      {statusIcon(review.status)} {review.status}
                    </span>
                    <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full">
                      <Star size={12} className="text-amber-400 fill-amber-400" />
                      <span className="text-xs font-bold text-amber-700">{review.overall_rating}</span>
                    </div>
                  </div>
                </div>

                {/* Rating Breakdown */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 bg-gray-50 rounded-lg p-3">
                  {[
                    { key: "quality", label: "Quality" },
                    { key: "taste", label: "Taste" },
                    { key: "packaging", label: "Packaging" },
                    { key: "delivery_punctuality", label: "Delivery" },
                  ].map((p) => (
                    <div key={p.key} className="text-center">
                      <p className="text-[9px] text-gray-400 mb-0.5">{p.label}</p>
                      {renderStars(review.ratings?.[p.key] || 0)}
                    </div>
                  ))}
                </div>

                {/* Comment */}
                {review.comment && (
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 mb-3 italic">"{review.comment}"</p>
                )}

                {/* Order/Item Info */}
                <p className="text-[10px] text-gray-400 mb-3">
                  Order: <span className="font-semibold text-green-600">{review.order_number || review.order_id?.substring(0, 20)}</span>
                  {review.item_name && <> | Item: <span className="font-medium text-gray-700">{review.item_name}</span></>}
                </p>

                {/* Actions */}
                <div className="flex gap-2">
                  {review.status !== "approved" && (
                    <Button size="sm" onClick={() => updateStatus(review.review_id, "approved")} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`approve-${review.review_id}`}>
                      <CheckCircle size={12} className="mr-1" /> Approve
                    </Button>
                  )}
                  {review.status !== "rejected" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(review.review_id, "rejected")} className="text-red-600 border-red-200 hover:bg-red-50 text-xs" data-testid={`reject-${review.review_id}`}>
                      <XCircle size={12} className="mr-1" /> Reject
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => deleteReview(review.review_id)} className="text-gray-500 border-gray-200 hover:bg-gray-50 text-xs ml-auto" data-testid={`delete-${review.review_id}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
